import React from 'react';
import { FaCalendarAlt, FaArrowRight, FaHome, FaShieldAlt } from 'react-icons/fa';

interface Section {
  id: number;
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  content: string;
  steps?: {
    number: number;
    title: string;
    description: string;
  }[];
}

interface RightSectionProps {
  activeSection: number;
  sections: Section[];
  setActiveSection: (index: number) => void;
  sectionVisibility?: boolean[];
  videoUrl: string;
  loading: boolean;
  showPrivacyPolicy?: boolean;
  showEvents?: boolean;
  onHomeClick: () => void;
  onPrivacyPolicyClick?: () => void;
  onEventsClick?: () => void;
  config?: { sections: { showHome: boolean; showPrivacyPolicy: boolean } };
}

const ICON_COLORS: { bg: string; color: string; activeBg: string; borderColor: string }[] = [
  { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', activeBg: 'rgba(59,130,246,0.25)', borderColor: 'rgba(59,130,246,0.25)' },
  { bg: 'rgba(16,185,129,0.15)', color: '#34d399', activeBg: 'rgba(16,185,129,0.25)', borderColor: 'rgba(16,185,129,0.25)' },
  { bg: 'rgba(236,72,153,0.15)', color: '#f472b6', activeBg: 'rgba(236,72,153,0.25)', borderColor: 'rgba(236,72,153,0.25)' },
  { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24', activeBg: 'rgba(245,158,11,0.25)', borderColor: 'rgba(245,158,11,0.25)' },
  { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa', activeBg: 'rgba(139,92,246,0.25)', borderColor: 'rgba(139,92,246,0.25)' },
];

const RightSection: React.FC<RightSectionProps> = ({ 
  activeSection, 
  sections, 
  setActiveSection,
  sectionVisibility,
  videoUrl, 
  loading,
  showPrivacyPolicy = false,
  showEvents = false,
  onHomeClick,
  onPrivacyPolicyClick,
  onEventsClick,
  config
}) => {
  const visibility = sectionVisibility ?? [true, true, true, true, true];
  const showHome = config?.sections?.showHome ?? true;
  const showPrivacy = config?.sections?.showPrivacyPolicy ?? true;
  const compactTitle = (t: string) => t.replace(/^(The |For )/i, '');

  return (
    <div className="hidden lg:block lg:w-[20%] z-20 space-y-4 animate-fade-in flex-shrink-0 pt-0 lg:pt-8" style={{ animationDelay: '0.3s' }}>
      {/* Nav card with bright animated border */}
      <div className="lg:mx-2 xl:mx-6 2xl:mx-12">
        <div className="group relative rounded-2xl overflow-hidden" style={{ perspective: '600px' }}>
          {/* Animated ring border — matches left panel */}
          <div className="absolute inset-0 rounded-2xl" style={{
            background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #0ea5e9, #ffffff, #3b82f6)',
            animation: 'ring-spin 3s linear 1 forwards',
            padding: '1.5px'
          }}>
            <div className="w-full h-full rounded-2xl bg-BLACK/80" />
          </div>
          {/* Outer glow */}
          <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-700 pointer-events-none" style={{
            background: 'conic-gradient(from 90deg, #3b82f6, #6366f1, #0ea5e9, #3b82f6)',
            filter: 'blur(16px)'
          }} />

          <div className="relative rounded-2xl p-3 xl:p-4 backdrop-blur-2xl bg-BLACK/50">
            {/* Current section info */}
            <div className="mb-3 pb-3 border-b border-WHITE/15">
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <div className="text-WHITE/40 text-[10px] font-medium tracking-wider uppercase mb-0.5">
                    {showPrivacyPolicy ? 'Privacy' : `${String(activeSection + 1).padStart(2, '0')} / ${sections.length}`}
                  </div>
                  <div className="text-WHITE font-bold text-sm truncate">
                    {showPrivacyPolicy ? 'Privacy Policy' : sections[activeSection].title}
                  </div>
                </div>
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border"
                  style={{
                    background: 'linear-gradient(135deg, rgba(96,165,250,0.2), rgba(129,140,248,0.2))',
                    borderColor: 'rgba(96,165,250,0.35)'
                  }}
                >
                  <span className="text-blue-400 text-[10px]">→</span>
                </div>
              </div>
              <p className="text-WHITE/50 text-[11px] line-clamp-2 mt-1 leading-relaxed">
                {showPrivacyPolicy ? 'Your privacy and data security matter to us' : sections[activeSection].content}
              </p>
            </div>

            {/* Navigation buttons */}
            <div className="flex flex-col gap-1.5">
              {sections.map((section, i) => {
                if (!visibility[i]) return null;
                const Icon = section.icon;
                const isActive = activeSection === i && !showPrivacyPolicy && !showEvents;
                const color = ICON_COLORS[i % ICON_COLORS.length];
                return (
                  <button
                    key={i}
                    onClick={() => setActiveSection(i)}
                    className={`group/btn flex items-center gap-2 px-2.5 py-2 xl:py-2.5 rounded-xl transition-all duration-300 min-w-0 ${
                      isActive
                        ? 'bg-WHITE/[0.12] border border-WHITE/25 shadow-[0_0_16px_rgba(255,255,255,0.08)]'
                        : 'bg-WHITE/[0.04] border border-transparent hover:bg-WHITE/[0.08] hover:border-WHITE/10'
                    }`}
                  >
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 border"
                      style={{
                        background: isActive ? color.activeBg : color.bg,
                        borderColor: isActive ? color.borderColor : 'transparent',
                      }}
                    >
                      <Icon size={12} className="flex-shrink-0" style={{ color: color.color }} />
                    </div>
                    <span className={`text-[11px] xl:text-xs font-semibold truncate transition-colors ${
                      isActive ? 'text-WHITE' : 'text-WHITE/60 group-hover/btn:text-WHITE/85'
                    }`} title={section.title}>{compactTitle(section.title)}</span>
                    {isActive && <FaArrowRight size={8} className="ml-auto text-WHITE/40 flex-shrink-0" />}
                  </button>
                );
              })}

              {onEventsClick && (
                <button
                  onClick={onEventsClick}
                  className={`group/btn flex items-center gap-2 px-2.5 py-2 xl:py-2.5 rounded-xl transition-all duration-300 w-full min-w-0 ${
                    showEvents
                      ? 'bg-WHITE/[0.12] border border-WHITE/25 shadow-[0_0_16px_rgba(255,255,255,0.08)]'
                      : 'bg-WHITE/[0.04] border border-transparent hover:bg-WHITE/[0.08] hover:border-WHITE/10'
                  }`}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 border"
                    style={{
                      background: showEvents ? 'rgba(6,182,212,0.25)' : 'rgba(6,182,212,0.15)',
                      borderColor: showEvents ? 'rgba(6,182,212,0.25)' : 'transparent',
                    }}
                  >
                    <FaCalendarAlt size={12} className="flex-shrink-0" style={{ color: '#22d3ee' }} />
                  </div>
                  <span className={`text-[11px] xl:text-xs font-semibold transition-colors ${
                    showEvents ? 'text-WHITE' : 'text-WHITE/60 group-hover/btn:text-WHITE/85'
                  }`}>Events</span>
                  {showEvents && <FaArrowRight size={8} className="ml-auto text-WHITE/40 flex-shrink-0" />}
                </button>
              )}
              
              {(showHome || showPrivacy) && (
                <>
                  <div className="relative h-px w-full my-2">
                    <div className="absolute inset-0 bg-WHITE/10" />
                    <div className="absolute inset-y-0 left-[15%] right-[15%]" style={{ background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.4), rgba(52,211,153,0.3), transparent)' }} />
                  </div>
                  {showHome && (
                    <button
                      onClick={onHomeClick}
                      className="group/btn flex items-center gap-2 px-2.5 py-2 rounded-xl transition-all duration-300 bg-WHITE/[0.04] border border-transparent hover:bg-WHITE/[0.08] hover:border-WHITE/10 w-full"
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all border border-transparent"
                        style={{ background: 'rgba(14,165,233,0.15)' }}
                      >
                        <FaHome size={12} style={{ color: '#38bdf8' }} />
                      </div>
                      <span className="text-[11px] xl:text-xs font-semibold text-WHITE/60 group-hover/btn:text-WHITE/85 transition-colors">Home</span>
                    </button>
                  )}
                  {showPrivacy && (
                    <button
                      onClick={onPrivacyPolicyClick}
                      className={`group/btn flex items-center gap-2 px-2.5 py-2 rounded-xl transition-all duration-300 w-full ${
                        showPrivacyPolicy
                          ? 'bg-WHITE/[0.12] border border-WHITE/25 shadow-[0_0_16px_rgba(255,255,255,0.08)]'
                          : 'bg-WHITE/[0.04] border border-transparent hover:bg-WHITE/[0.08] hover:border-WHITE/10'
                      }`}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all border"
                        style={{
                          background: showPrivacyPolicy ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.15)',
                          borderColor: showPrivacyPolicy ? 'rgba(99,102,241,0.25)' : 'transparent',
                        }}
                      >
                        <FaShieldAlt size={12} style={{ color: '#818cf8' }} />
                      </div>
                      <span className={`text-[11px] xl:text-xs font-semibold transition-colors ${
                        showPrivacyPolicy ? 'text-WHITE' : 'text-WHITE/60 group-hover/btn:text-WHITE/85'
                      }`}>Privacy Policy</span>
                      {showPrivacyPolicy && <FaArrowRight size={8} className="ml-auto text-WHITE/40 flex-shrink-0" />}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Video section */}
      <div className="flex justify-center">
        <div className="lg:mx-2 xl:mx-6 2xl:mx-12 w-full">
          <div className="relative rounded-2xl overflow-hidden" style={{ padding: '1.5px' }}>
            <div className="absolute inset-0 rounded-2xl" style={{
              background: 'conic-gradient(from 180deg, #3b82f6, #0ea5e9, #ffffff, #3b82f6)',
              animation: 'ring-spin 3s linear 1 forwards'
            }} />
            <div className="relative rounded-2xl overflow-hidden bg-BLACK/80 backdrop-blur-md">
              {loading ? (
                <div className="flex items-center justify-center h-120">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-WHITE/30 border-t-WHITE"></div>
                </div>
              ) : videoUrl ? (
                <video 
                  className="w-full h-140 object-cover rounded-lg" 
                  src={videoUrl} 
                  controls 
                  muted 
                  loop 
                  playsInline 
                  autoPlay
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RightSection;
